<template>
  <page-wrapper :title="title" :hasPad="true" :noPadTop="true" :noPadBtm="true">
    <div class="wrapper">
      <package-editor
        class="mb-4"
        v-model="form"
        :imageUrl="imageUrl"
        :thumbnailUrl="thumbnailUrl"
        :targets="targets"
        :sellerTradeParties="sellerTradeParties"
        :paymentCampaigns="paymentCampaigns"
        @changedHasOptionPackage="handleSetupPricings"
        @selectedService="handleSetupPricings"
      ></package-editor>
      <div class="text-right">
        <sc-button variant="primary" @click="handleClickSave">บันทึก</sc-button>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import PackageEditor from "../components/PackageEditor";
import { baanRueJaiClient } from "@/dependencies";
import { uploadPublicImage } from "@/god";
import { isNil, isEmpty, head, tail } from "ramda";
import { DateTime } from "luxon";
import { toOptionalDateTime, toOptionalISOString } from "@/lib/dateTime";
import { toCamelCaseObject } from "@/lib/util";
import {
  convertStangToBathAndRoundTwoDecimal,
  convertBathToStangAndRoundTwoDecimal
} from "@/lib/formatter";

export default {
  components: {
    PageWrapper,
    ScButton,
    PackageEditor
  },
  data() {
    const form = {
      name: "",
      receiptName: "",
      target: "สำหรับบ้าน",
      price: 0,
      pricings: [],
      billingCycle: 1,
      detail: "",
      sellerTradePartyId: null,
      termsAndConditions: "",
      services: [],
      projects: [],
      homes: [],
      imageFile: null,
      thumbnailFile: null,
      isSecret: false,
      isExpireAfterAppointment: false,
      noteLabel: null,
      registrationForm: null,
      hasRegistrationForm: false,
      hasOptionPackage: false,
      packageCollection: null,
      purchasingLimit: null,
      originalPrice: null,
      purchaseSuccessTitle: null,
      purchaseSuccessMessage: null,
      expiredAt: null,
      packageOptions: null,
      minimumQuantity: 1,
      maximumQuantity: null,
      newServicePrices: [],
      newOptionVariantPrices: [],
      isReadOnlyOptionPrice: true,
      expireDuration: 1,
      packageAlbums: [],
      hasAdvanceNotice: false,
      advanceNoticeDuration: null,
      advanceNoticeMessage: null,
      isIgnoreCountCoupon: false,
      paymentCampaignIds: [],
      isNotSendReceipt: false,
    };

    const imageUrl = null;
    const thumbnailUrl = null;
    const targets = [];
    const sellerTradeParties = [];
    const paymentCampaigns = [];
    const title = "เพิ่ม Package";

    return {
      form,
      imageUrl,
      thumbnailUrl,
      targets,
      sellerTradeParties,
      paymentCampaigns,
      title
    };
  },
  watch: {
    form(form, oldForm) {
      if (this.form.imageFile !== oldForm.imageFile) {
        this.imageUrl = this.form.imageFile;
      }

      if (this.form.thumbnailFile !== oldForm.thumbnailFile) {
        this.thumbnailUrl = URL.createObjectURL(this.form.thumbnailFile);
      }

      if (!isNil(this.form.registrationForm)) {
        this.form.registrationForm.sections.map((section) => {
          section.inputs.map((input) => {
            input.watermarkUrl = !isNil(input.watermarkFile)
              ? URL.createObjectURL(input.watermarkFile)
              : input.watermarkUrl;
          });
        });
      }

      if (this.form.services !== oldForm.services) {
        this.createPackageOptionPrice();
      }
    }
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    async setupData() {
      await this.setupTargets();
      await this.setupSellerTradeParties();
      await this.setupPaymentCampaigns();
      await this.setupForm();
    },
    async setupTargets() {
      const targets = await this.fetchTargets();

      this.targets = targets;
    },
     async setupPaymentCampaigns() {
      const paymentCampaigns = await this.fetchPaymentCampaigns();

      this.paymentCampaigns = paymentCampaigns
    },
    async setupSellerTradeParties() {
      const sellerTradeParties = await this.fetchSellerTradeParties();

      this.sellerTradeParties = sellerTradeParties
    },
    async setupForm() {
      if (!this.$route.query.id) {
        return;
      }

      const data = await this.fetchData();

      this.setupPackageForm(data);
    },
    async fetchData() {
      const { id } = this.$route.query;

      const response = await baanRueJaiClient.admin.subscriptions.recommend_packages.get(
        id
      );

      return response.data.result;
    },
    fetchTargets() {
      return Promise.resolve([
        { id: "0", name: "สำหรับบ้าน" },
        { id: "1", name: "สำหรับคอนโด" },
        { id: "2", name: "สำหรับบ้านขนาดใหญ่" }
      ]);
    },
    async fetchSellerTradeParties() {
      const response = await baanRueJaiClient.admin.subscriptions.receipts.getSellerTradeParties();

      return response.data.result;
    },
    async fetchPaymentCampaigns() {
      const response = await baanRueJaiClient.admin.payment.getPaymentCampaigns();

      return response.data.result;
    },
    setupPackageForm(data) {
      const {
        name,
        receipt_name: receiptName,
        detail,
        target,
        price,
        billing_cycle: billingCycle,
        terms_and_conditions: termsAndConditions,
        seller_trade_party_id: sellerTradePartyId,
        image_url: imageUrl,
        services,
        projects,
        homes,
        thumbnail_url: thumbnailUrl,
        is_secret: isSecret,
        is_expire_after_appointment: isExpireAfterAppointment,
        note_label,
        registration_form,
        subpackages,
        purchasing_limit: purchasingLimit,
        purchase_success_title: purchaseSuccessTitle,
        purchase_success_message: purchaseSuccessMessage = null,
        expired_at: expiredAt,
        package_albums: packageAlbums,
        has_advance_notice: hasAdvanceNotice,
        advance_notice_message: advanceNoticeMessage,
        advance_notice_days: advanceNoticeDuration,
        is_ignore_count_coupon: isIgnoreCountCoupon,
        payment_campaign_ids: paymentCampaignIds,
        is_required_issue_receipt: isRequiredIssueReceipt,
      } = data;

      let allImageToAlbum = []
      if (imageUrl && (!packageAlbums || packageAlbums.length === 0)) {
        allImageToAlbum = [imageUrl]
      } else if (
        packageAlbums
        && packageAlbums.length > 0
        && imageUrl
      ) {
        allImageToAlbum = packageAlbums
        allImageToAlbum.unshift(imageUrl)
      }

      this.form = {
        ...this.form,
        name,
        receiptName,
        detail,
        target,
        price: convertBathToStangAndRoundTwoDecimal(price),
        billingCycle,
        termsAndConditions,
        sellerTradePartyId,
        services: services.map((service) => ({
          ...service,
          iconUrl: service.icon_url,
          provider: {
            ...service.provider,
            logoUrl: service.provider.logo_url,
          }
        })),
        projects: projects.map((project) => ({
          ...project,
          projectId: project.project_id
        })),
        homes: homes.map(home => ({
          ...home
        })),
        imageFile: null,
        thumbnailFile: null,
        isSecret,
        isExpireAfterAppointment,
        noteLabel: note_label,
        registrationForm: toCamelCaseObject(registration_form),
        hasRegistrationForm: registration_form ? true : false,
        packageCollection: !isEmpty(subpackages)
          ? subpackages.map((item) => item.id).toString()
          : null,
        purchasingLimit,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt: toOptionalDateTime(expiredAt),
        packageAlbums,
        allImageToAlbum,
        hasAdvanceNotice,
        advanceNoticeMessage,
        advanceNoticeDuration,
        isIgnoreCountCoupon,
        paymentCampaignIds,
        isRequiredIssueReceipt,
        isNotSendReceipt: !isRequiredIssueReceipt,
      };
      this.imageUrl = imageUrl;
      this.thumbnailUrl = thumbnailUrl;
    },
    async handleClickSave() {
      if (this.isOriginalPriceLessThanOrEqualPrice()) {
        alert("ไม่สามารถบันทึกได้เนื่องจากราคาเต็มน้อยกว่าหรือเท่ากับราคาที่ลดแล้ว");
        return;
      }

      if (this.form.price < 0) {
        alert("ไม่สามารถบันทึกได้เนื่องจากใส่ราคาไม่ถูกต้อง");
        return;
      }

      if (!isNil(this.form.price.toString().split(".")[1])) {
        if (this.form.price.toString().split(".")[1].length > 2) {
          alert("ไม่สามารถบันทึกได้เนื่องจากราคามีทศนิยมมากกว่า 2 ตำแหน่ง");
          return;
        }
      }

      if (this.form.billingCycle < 0) {
        alert("ไม่สามารถบันทึกได้เนื่องจากใส่รอบตัดบิลไม่ถูกต้อง");
        return;
      }

      if (this.form.maximumQuantity < 1) {
        alert("กรุณาใส่จำนวนที่ซื้อได้สูงสุด");
        return;
      }

      if (this.form.hasAdvanceNotice === true) {
        if (
        this.form.advanceNoticeDuration > 0
        && 
        (
          isEmpty(this.form.advanceNoticeMessage)
          || isNil(this.form.advanceNoticeMessage)
        )
        ) {
          alert("กรุณาใส่ขอความแจ้งเตือน");
          return;
        }

        if (this.form.advanceNoticeDuration < 1) {
          alert("กรุณาใส่จำนวนวันแจ้งเตือนล่วงหน้า");
          return;
        }
      }

      if (isNil(this.form.sellerTradePartyId)) {
        alert("ไม่สามารถบันทึกได้เนื่องจากยังไม่ได้เลือกผู้จัดหาบริการ");
        return;
      }

      if (isEmpty(this.form.services) && isNil(this.form.packageCollection)) {
        alert("กรุณาใส่รายการบริการ");
        return;
      }

      if (
        this.isEmptyServicePrice(this.form.packageOptions)
        || this.isEmptyServiceOptionsPrice(this.form.packageOptions)
      ) {
        alert("กรุณาใส่ราคาขายรายครั้งให้ครบ");
        return;
      }

      if (!this.form.thumbnailFile && this.thumbnailUrl) {
        alert('กรุณาใส่รูป Thumbnail');
        return;
      }

      try {
        await this.createPackage();
        this.$router.push({ name: "PackageListPage" });
      } catch (err) {
        if (
          err.response
          && err.response.status
          && err.response.status === 400
        ) {
          alert(err.response.data.message);
          console.error(err);
        } else {
          alert(err.message);
          console.error(err);
        }
      }
    },
    handleSetupPricings() {
      if (this.form.hasOptionPackage && !isEmpty(this.form.services)) {
        const options = this.getOptions(this.form.services);

        this.setupPricings(options);
      } else {
        this.form.pricings = [];
      }
    },
    getOptions(services) {
      const options = !isEmpty(services)
        ? services.flatMap((service) =>
            service.options.map((option) =>
              option.variants.map((variant) => ({
                service: service.name,
                option: option.title,
                variant: variant.title,
                isSale: variant.is_sale
              }))
            )
          )
        : [];

      return options;
    },
    setupPricings(options) {
      const pricingPatterns = this.createPricingPatterns(options);

      this.form.pricings = !isEmpty(pricingPatterns)
        ? pricingPatterns.map((pattern) => ({
            variants: pattern,
            isSell: true,
            isSale: false,
            originalPrice: null,
            price: 0
          }))
        : [];
    },
    createPricingPatterns(options) {
      if (isEmpty(options)) {
        return [];
      }

      if (options.length === 1) {
        return head(options).map((subOption) => [subOption]);
      } else if (options.length >= 1) {
        const firstOption = head(options);
        const otherOptions = tail(options);
        const patternOptions = this.createPricingPatterns(
          otherOptions
        ).flatMap((subOtherOption) =>
          firstOption.map((subOption) => [subOption, ...subOtherOption])
        );

        return patternOptions;
      }
    },
    async createPackage() {
      const {
        name,
        receiptName,
        target,
        price,
        pricings,
        billingCycle,
        detail,
        termsAndConditions,
        sellerTradePartyId,
        services,
        projects,
        homes,
        imageFile,
        thumbnailFile,
        isSecret,
        isExpireAfterAppointment,
        noteLabel,
        registrationForm,
        packageCollection,
        purchasingLimit,
        originalPrice,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt,
        packageOptions,
        minimumQuantity,
        maximumQuantity,
        expireDuration,
        packageAlbums,
        hasAdvanceNotice,
        advanceNoticeMessage,
        advanceNoticeDuration,
        isIgnoreCountCoupon,
        paymentCampaignIds,
        isNotSendReceipt
      } = this.form;

      const newPackageOptions = await this.setupPackageOptionsMultiplyPrice(
        packageOptions
      );

      const expireDateDuration =
        billingCycle === 0 ? +expireDuration : undefined;

      const subpackageIds = packageCollection
        ? packageCollection.split(",").map((id) => id.trim())
        : [];
      const image = imageFile || this.imageUrl;
      const thumbnail = thumbnailFile
        ? await uploadPublicImage(thumbnailFile)
        : this.thumbnailUrl;
      const serviceIds = services.map(({ id }) => id);
      const projectIds = projects.map(({ projectId }) => projectId);
      const homeIds= homes.map(({ home_id }) => home_id);
      // PROBLEM : too many async await and Promise.all
      const registration_form = registrationForm
        ? {
            ...registrationForm,
            sections: await Promise.all(
              registrationForm.sections.map(async (section) => {
                return {
                  ...section,
                  inputs: await Promise.all(
                    section.inputs.map(async (input) => {
                      return {
                        isRequired: input.isRequired,
                        prefillKey: input.prefillKey,
                        title: input.title,
                        type: input.type,
                        watermarkUrl: input.watermarkFile
                          ? await uploadPublicImage(
                              input.watermarkFile,
                              name,
                              "article"
                            )
                          : null
                      };
                    })
                  )
                };
              })
            )
          }
        : null;

      const params = {
        name,
        receiptName,
        target,
        price: convertStangToBathAndRoundTwoDecimal(price),
        pricings,
        billingCycle,
        detail,
        termsAndConditions,
        sellerTradePartyId,
        serviceIds,
        projectIds,
        homeIds,
        image,
        thumbnail,
        isSecret,
        isExpireAfterAppointment,
        noteLabel,
        registrationForm: registration_form,
        subpackageIds,
        purchasingLimit,
        originalPrice: originalPrice !== null
          ? convertStangToBathAndRoundTwoDecimal(originalPrice)
          : null,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt: toOptionalISOString(expiredAt),
        packageOptions: newPackageOptions,
        minimumQuantity: parseInt(minimumQuantity),
        maximumQuantity: parseInt(maximumQuantity),
        expireDuration: expireDateDuration,
        packageAlbums: undefined,
        hasAdvanceNotice,
        advanceNoticeMessage,
        advanceNoticeDuration: parseInt(advanceNoticeDuration),
        isIgnoreCountCoupon,
        paymentCampaignIds,
        isNotSendReceipt
      };

      if (packageAlbums && packageAlbums.length > 0) {
        const albumPack = packageAlbums.filter((album) => album !== imageFile)

        if (albumPack && albumPack.length > 0) {
          params.packageAlbums = albumPack
        }
      }

      const response = await baanRueJaiClient.admin.subscriptions.recommend_packages.post(
        params
      );

      return response.data.result;
    },
    isOriginalPriceLessThanOrEqualPrice() {
      return (
        this.form.originalPrice !== null &&
        this.form.originalPrice <= this.form.price
      );
    },
    createPackageOptionPrice() {
      const packageOptions = this.form.services.map((serviceDetail) => {
        const serviceId = serviceDetail.id;
        const serviceTitle = serviceDetail.name;
        const isAppointment = serviceDetail.quota > 0;
        let serviceMainOption = null;
        let serviceExtraOption = null;
        let serviceOptionVariants = null;
        let serviceAppointmentPrices = null;

        if (serviceDetail.options && serviceDetail.options.service_options) {
          serviceDetail.options.service_options.forEach((serviceOption) => {
            if (serviceOption.extra_option === true) {
              serviceExtraOption = {
                id: serviceOption.id,
                title: serviceOption.title,
              };
            } else {
              serviceMainOption = {
                id: serviceOption.id,
                title: serviceOption.title,
              };
            }
          });

          serviceOptionVariants = serviceDetail.options.service_option_costs.map(
            (serviceOptionCost) => {
              const variants = serviceOptionCost.combination_costs.map(
                (combineCost, combineCostIndex) => {
                  const extraOption = combineCostIndex === 0 ? false : true;

                  const appointmentPrices = combineCost.appointment_costs.map(
                    (appointmentCost) => {
                      return {
                        appoint_no: appointmentCost.appoint_no,
                        appoint_price: null,
                      };
                    }
                  );

                  return {
                    id: combineCost.id,
                    title: combineCost.title,
                    extra_option: extraOption,
                    price: null,
                    original_price: null,
                    is_out_of_stock: combineCost.is_out_of_stock,
                    is_discount: false,
                    is_not_sale: combineCost.is_not_sale,
                    appointment_prices: appointmentPrices,
                  };
                }
              );

              return {
                id: serviceOptionCost.id,
                title: serviceOptionCost.title,
                variants,
              };
            }
          );
        } else {
          const qouta = serviceDetail.quota > 0 ? serviceDetail.quota : 1;

          const fillAppointment = Array.from({ length: qouta }).fill();

          serviceAppointmentPrices = fillAppointment.map((item, index) => {
            return {
              appoint_no: index + 1,
              appoint_price: null,
            };
          });
        }

        return {
          service_id: serviceId,
          service_title: serviceTitle,
          service_main_option: serviceMainOption,
          service_extra_option: serviceExtraOption,
          service_option_variants: serviceOptionVariants,
          service_price: null,
          service_original_price: null,
          is_out_of_stock: false,
          is_discount: false,
          is_not_sale: false,
          service_appointment_prices: serviceAppointmentPrices,
          is_appointment: isAppointment
        };
      });

      this.form.packageOptions = packageOptions;
    },
    isEmptyServiceOptionsPrice(packageOptions) {
      const isOptionPrice = packageOptions.some((packageOption) => {
        const isPrice = packageOption.service_option_variants
          ? packageOption.service_option_variants.some((optionVariant) => {
              return optionVariant.variants.some((variant) => !variant.price);
            })
          : false;

        return isPrice;
      });

      return isOptionPrice;
    },
    isEmptyServicePrice(packageOptions) {
      const hasEmptyPrice = packageOptions
        ? packageOptions.some(
            (packageOption) =>
              !packageOption.service_option_variants &&
              !packageOption.service_price
          )
        : false;

      return hasEmptyPrice;
    },
    async setupPackageOptionsMultiplyPrice(packageOptionsDetail) {
      if (!packageOptionsDetail) return undefined;

      const packageOptions = packageOptionsDetail.map((packageOption) => {
        let serviceOptionVariants;
        let serviceAppointmentPrices;

        const servicePrice = convertStangToBathAndRoundTwoDecimal(packageOption.service_price)
        const serviceOriginalPrice = convertStangToBathAndRoundTwoDecimal(packageOption.service_original_price)

        if(
          packageOption.service_appointment_prices
          && packageOption.service_appointment_prices.length > 0
        ) {
          serviceAppointmentPrices = packageOption.service_appointment_prices.map(
            (serviceAppointmentPrice) => {
              const appointPrice = convertStangToBathAndRoundTwoDecimal(serviceAppointmentPrice.appoint_price)

              return {
                appoint_no: serviceAppointmentPrice.appoint_no,
                appoint_price: appointPrice
              };
            }
          );
        }

        if (packageOption.service_option_variants) {
          serviceOptionVariants = packageOption.service_option_variants.map(
            (optionVariant) => {
              const variants = optionVariant.variants.map((variant) => {
                const originalPrice = convertStangToBathAndRoundTwoDecimal(variant.original_price)
                const price = convertStangToBathAndRoundTwoDecimal(variant.price)

                const isEmptyIsNotSaleNewOption = this.newOptionVariants
                  ? this.newOptionVariants.some(
                      (newOptionVariant) => newOptionVariant === variant.id
                    )
                  : false;

                const isNotSale = price >= 0 && isEmptyIsNotSaleNewOption
                    ? false
                    : variant.is_not_sale;

                const appointmentPrices = variant.appointment_prices.map(
                    (appointmentPrice) => {
                  const appointPrice = convertStangToBathAndRoundTwoDecimal(
                    appointmentPrice.appoint_price
                  )

                  return {
                    appoint_no: appointmentPrice.appoint_no,
                    appoint_price: appointPrice
                  };
                });

                return {
                  extra_option: variant.extra_option,
                  id: variant.id,
                  is_discount: variant.is_discount,
                  is_not_sale: isNotSale,
                  is_out_of_stock: variant.is_out_of_stock,
                  original_price: originalPrice,
                  price: price,
                  title: variant.title,
                  appointment_prices: appointmentPrices
                };
              });

              return {
                id: optionVariant.id,
                title: optionVariant.title,
                variants
              };
            }
          );
        }

        return {
          is_discount: packageOption.is_discount,
          is_not_sale: packageOption.is_not_sale,
          is_out_of_stock: packageOption.is_out_of_stock,
          service_extra_option: packageOption.service_extra_option,
          service_id: +packageOption.service_id,
          service_main_option: packageOption.service_main_option,
          service_option_variants: serviceOptionVariants,
          service_original_price: serviceOriginalPrice,
          service_price: servicePrice,
          service_title: packageOption.service_title,
          service_appointment_prices: serviceAppointmentPrices,
          is_appointment: packageOption.is_appointment
        };
      });

      return packageOptions;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: $space 0;
}

.editor {
  margin-bottom: $space;
}
</style>



// WEBPACK FOOTER //
// src/features/subscriptions/modules/packages/pages/PackageCreatePage.vue